import { ORG_STRUCTURE_LEVELS, OrgStructureLevel } from 'services/OrgStructureHelper';

type Labels = Record<string, string> | undefined;

interface LabelValuesResponse {
    projectLabelValues?: { value: string; deviceCount: number }[];
}

interface SensorServiceLike {
    listLabelValues(labelKey: string, labelFilter?: Record<string, string>): PromiseLike<LabelValuesResponse>;
}

interface QLike {
    when<T>(value: T | PromiseLike<T>): PromiseLike<T>;
}

type TimeoutLike = (callback: () => void, delay: number) => unknown;

// Delay before saving on blur, so clicking a suggestion lands first
const BLUR_SAVE_DELAY_MS = 200;

export default class OrgStructureLabelsController {
    // declare: type-only, erased by tsc — real class fields break this project's webpack setup
    declare static $inject: string[];

    declare private SensorService: SensorServiceLike;

    declare private $q: QLike;

    declare private $timeout: TimeoutLike;

    // Bindings
    declare labels: Labels;

    declare canUpdate: boolean;

    declare onSave: (locals: { key: string; value: string }) => unknown;

    // State
    declare levels: readonly OrgStructureLevel[];

    declare values: Record<string, string>;

    declare suggestions: string[];

    declare filteredSuggestions: string[];

    declare dropdownKey: string | null;

    declare private focusedKey: string | null;

    declare private savingValues: Record<string, string>;

    constructor(SensorService: SensorServiceLike, $q: QLike, $timeout: TimeoutLike) {
        this.SensorService = SensorService;
        this.$q = $q;
        this.$timeout = $timeout;
        this.levels = ORG_STRUCTURE_LEVELS;
        this.values = {};
        this.suggestions = [];
        this.filteredSuggestions = [];
        this.dropdownKey = null;
        this.focusedKey = null;
        this.savingValues = {};
    }

    $onChanges(): void {
        this.levels.forEach(level => this.syncValue(level.key));
    }

    onFocus(key: string): void {
        this.focusedKey = key;
        this.suggestions = [];
        this.SensorService.listLabelValues(key, this.getParentLabelFilter(key)).then(response => {
            if (this.focusedKey !== key) return;
            // Most used values first
            this.suggestions = [...(response.projectLabelValues || [])]
                .sort((a, b) => b.deviceCount - a.deviceCount)
                .map(item => item.value);
            this.filterSuggestions(key);
        }, error => {
            console.error('Error fetching label values:', error); // eslint-disable-line no-console
            this.suggestions = [];
        });
    }

    onBlur(key: string): void {
        if (this.focusedKey === key) {
            this.focusedKey = null;
        }
        this.$timeout(() => this.save(key), BLUR_SAVE_DELAY_MS);
    }

    filterSuggestions(key: string): void {
        const query = (this.values[key] || '').toLowerCase();
        this.filteredSuggestions = this.suggestions.filter(value => value.toLowerCase().includes(query));
        this.dropdownKey = this.filteredSuggestions.length > 0 ? key : null;
    }

    selectSuggestion(key: string, value: string): void {
        this.values[key] = value;
        this.save(key);
    }

    save(key: string): void {
        if (this.dropdownKey === key) {
            this.dropdownKey = null;
        }
        const value = (this.values[key] || '').trim();
        this.values[key] = value;

        // Skip unchanged values and repeats of a save still in flight
        const pendingValue = this.savingValues[key];
        if (value === (pendingValue !== undefined ? pendingValue : this.getLabelValue(key))) return;

        this.savingValues[key] = value;
        // The parent reports failures; either way the input shows what the labels now hold
        const done = () => {
            delete this.savingValues[key];
            this.syncValue(key);
        };
        this.$q.when(this.onSave({ key, value })).then(done, done);
    }

    private getLabelValue(key: string): string {
        return (this.labels && this.labels[key]) || '';
    }

    private syncValue(key: string): void {
        this.values[key] = this.getLabelValue(key);
    }

    // Narrows suggestions to the levels above, e.g. buildings in the selected hospital
    private getParentLabelFilter(key: string): Record<string, string> {
        const index = this.levels.findIndex(level => level.key === key);
        return this.levels.slice(0, index).reduce((filter, level) => {
            const value = this.getLabelValue(level.key);
            return value ? { ...filter, [level.key]: value } : filter;
        }, {});
    }
}

OrgStructureLabelsController.$inject = ['SensorService', '$q', '$timeout'];
